import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Dropdown,
  Option,
  Checkbox,
  Spinner,
  MessageBar,
  MessageBarBody,
  Text,
  TabList,
  Tab,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import BulkEditObligationsContent from './BulkEditObligationsContent';
import {
  useSalesTaxAdmins,
  usePreviewBulkClientUpdate,
  useBulkUpdateClients,
  STATE_TAX_OBLIGATION_OPTIONS,
} from '../../services/stateTaxClientsApi';
import type { BulkClientUpdateResult, OpenTaskReassignmentPreview } from '../../types';
import { CalendarDateInput } from '../dates/CalendarDateInput';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalM,
  },
  bar: {
    marginBottom: tokens.spacingVerticalS,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  taskList: {
    maxHeight: '220px',
    overflowY: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  taskRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase200,
    padding: '2px 0',
  },
  taskMeta: {
    color: tokens.colorNeutralForeground3,
  },
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The clients selected for bulk edit (id + display name). */
  selectedClients: { id: string; name: string }[];
  /** Called after a successful commit so the page can clear its selection. */
  onCommitted?: (result: BulkClientUpdateResult) => void;
}

type Phase = 'edit' | 'confirmReassign' | 'result';
type Mode = 'clients' | 'obligations';

const UNCHANGED = ''; // dropdown sentinel — "leave this field alone"

export default function BulkEditClientsDialog({ open, onOpenChange, selectedClients, onCommitted }: Props) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { accounts } = useMsal();
  const activeAccount = accounts[0];

  const { data: admins, isLoading: adminsLoading } = useSalesTaxAdmins();
  const previewMutation = usePreviewBulkClientUpdate();
  const bulkMutation = useBulkUpdateClients();

  const [mode, setMode] = useState<Mode>('clients');
  const [phase, setPhase] = useState<Phase>('edit');
  const [ownerId, setOwnerId] = useState<string>(UNCHANGED);
  const [frequency, setFrequency] = useState<string>(UNCHANGED);
  const [effectiveDate, setEffectiveDate] = useState<string>('');
  const [informationSource, setInformationSource] = useState<string>(UNCHANGED);
  const [formError, setFormError] = useState<string | null>(null);

  // Owner-reassignment confirmation state.
  const [affectedTasks, setAffectedTasks] = useState<OpenTaskReassignmentPreview[]>([]);
  const [reassignOpenTasks, setReassignOpenTasks] = useState(false);
  const [result, setResult] = useState<BulkClientUpdateResult | null>(null);

  // Reset everything whenever the dialog (re)opens.
  useEffect(() => {
    if (open) {
      setMode('clients');
      setPhase('edit');
      setOwnerId(UNCHANGED);
      setFrequency(UNCHANGED);
      setEffectiveDate('');
      setInformationSource(UNCHANGED);
      setFormError(null);
      setAffectedTasks([]);
      setReassignOpenTasks(false);
      setResult(null);
    }
  }, [open]);

  const clientIds = selectedClients.map((c) => c.id);
  const ownerName = admins?.find((a) => a.id === ownerId)?.name;
  const hasOwner = ownerId !== UNCHANGED;
  const hasFrequency = frequency !== UNCHANGED;
  const hasInfoSource = informationSource !== UNCHANGED;
  const isBusy = previewMutation.isPending || bulkMutation.isPending;

  function validate(): boolean {
    if (!hasOwner && !hasFrequency && !hasInfoSource) {
      setFormError(t('bulkEdit.validation.noField'));
      return false;
    }
    if (hasFrequency && !effectiveDate) {
      setFormError(t('bulkEdit.validation.effectiveDateRequired'));
      return false;
    }
    setFormError(null);
    return true;
  }

  async function commit(reassign: boolean) {
    setFormError(null);
    try {
      const res = await bulkMutation.mutateAsync({
        clientIds,
        ownerSystemUserId: hasOwner ? ownerId : undefined,
        ownerName: hasOwner ? ownerName : undefined,
        frequency: hasFrequency ? frequency : undefined,
        effectiveDate: hasFrequency ? effectiveDate : undefined,
        informationSource: hasInfoSource ? informationSource : undefined,
        reassignOpenTasks: reassign,
        changedByName: activeAccount?.name ?? activeAccount?.username,
        changedById: activeAccount?.localAccountId,
      });
      setResult(res);
      setPhase('result');
      onCommitted?.(res);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : t('bulkEdit.clients.failed'));
    }
  }

  async function handleApply() {
    if (!validate()) return;
    // When the owner is changing, look up which open tasks would be affected so the
    // staffer can decide whether to also re-stamp them (default is future-only).
    if (hasOwner) {
      try {
        const tasks = await previewMutation.mutateAsync(clientIds);
        if (tasks.length > 0) {
          setAffectedTasks(tasks);
          setReassignOpenTasks(false);
          setPhase('confirmReassign');
          return;
        }
      } catch (err) {
        setFormError(err instanceof Error ? err.message : t('bulkEdit.clients.previewFailed'));
        return;
      }
    }
    // No owner change, or no open tasks to worry about — commit straight away.
    await commit(false);
  }

  const clientCount = selectedClients.length;

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, data) => {
        if (!isBusy) onOpenChange(data.open);
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {phase === 'result'
              ? t('bulkEdit.clients.titleResult')
              : mode === 'obligations'
                ? t('bulkEdit.clients.titleObligations', { count: clientCount })
                : t('bulkEdit.clients.titleClients', { count: clientCount })}
          </DialogTitle>

          {/* Mode switch — only at the initial edit step so we never swap mid-flow. */}
          {phase === 'edit' && (
            <TabList
              selectedValue={mode}
              onTabSelect={(_e, d) => setMode(d.value as Mode)}
              style={{ marginBottom: tokens.spacingVerticalS }}
            >
              <Tab value="clients">{t('bulkEdit.clients.tabClients')}</Tab>
              <Tab value="obligations">{t('bulkEdit.clients.tabObligations')}</Tab>
            </TabList>
          )}

          {mode === 'obligations' ? (
            <BulkEditObligationsContent
              selectedClients={selectedClients}
              onOpenChange={onOpenChange}
              onCommitted={() => {}}
            />
          ) : (
          <>
          <DialogContent>
            {formError && (
              <MessageBar intent="error" className={styles.bar}>
                <MessageBarBody>{formError}</MessageBarBody>
              </MessageBar>
            )}

            {phase === 'edit' && (
              <>
                <MessageBar intent="info" className={styles.bar}>
                  <MessageBarBody>
                    {t('bulkEdit.clients.intro')}
                  </MessageBarBody>
                </MessageBar>
                <div className={styles.form}>
                  <Field label={t('bulkEdit.clients.owner')}>
                    <Dropdown
                      placeholder={adminsLoading ? t('bulkEdit.clients.loadingAdmins') : t('bulkEdit.dontChange')}
                      value={hasOwner ? (ownerName ?? '') : t('bulkEdit.dontChange')}
                      selectedOptions={hasOwner ? [ownerId] : []}
                      onOptionSelect={(_e, d) => setOwnerId(d.optionValue ?? UNCHANGED)}
                    >
                      <Option value={UNCHANGED}>{t('bulkEdit.dontChange')}</Option>
                      {(admins ?? []).map((a) => (
                        <Option key={a.id} value={a.id} text={a.name}>
                          {a.name}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>

                  <div className={styles.row}>
                    <Field label={t('bulkEdit.filingFrequency')}>
                      <Dropdown
                        placeholder={t('bulkEdit.dontChange')}
                        value={hasFrequency ? frequency : t('bulkEdit.dontChange')}
                        selectedOptions={hasFrequency ? [frequency] : []}
                        onOptionSelect={(_e, d) => setFrequency(d.optionValue ?? UNCHANGED)}
                      >
                        <Option value={UNCHANGED}>{t('bulkEdit.dontChange')}</Option>
                        {STATE_TAX_OBLIGATION_OPTIONS.filingFrequency.map((f) => (
                          <Option key={f} value={f}>
                            {f}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                    <Field
                      label={t('bulkEdit.effectiveDate')}
                      required={hasFrequency}
                      hint={hasFrequency ? t('bulkEdit.effectiveDateHint') : t('bulkEdit.effectiveDateRequiredHint')}
                    >
                      <CalendarDateInput value={effectiveDate} disabled={!hasFrequency} onChange={setEffectiveDate} />
                    </Field>
                  </div>

                  <Field label={t('bulkEdit.informationSource')}>
                    <Dropdown
                      placeholder={t('bulkEdit.dontChange')}
                      value={hasInfoSource ? informationSource : t('bulkEdit.dontChange')}
                      selectedOptions={hasInfoSource ? [informationSource] : []}
                      onOptionSelect={(_e, d) => setInformationSource(d.optionValue ?? UNCHANGED)}
                    >
                      <Option value={UNCHANGED}>{t('bulkEdit.dontChange')}</Option>
                      {STATE_TAX_OBLIGATION_OPTIONS.informationSource.map((s) => (
                        <Option key={s} value={s}>
                          {s}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                </div>
              </>
            )}

            {phase === 'confirmReassign' && (
              <div className={styles.form}>
                <MessageBar intent="warning" className={styles.bar}>
                  <MessageBarBody>
                    <Trans
                      i18nKey="bulkEdit.clients.reassignWarning"
                      count={affectedTasks.length}
                      values={{ owner: ownerName }}
                    />
                  </MessageBarBody>
                </MessageBar>
                <div className={styles.taskList}>
                  {affectedTasks.map((task) => (
                    <div key={task.taskId} className={styles.taskRow}>
                      <span>
                        {task.clientName} — {task.periodLabel}
                      </span>
                      <span className={styles.taskMeta}>
                        {task.status} · {task.currentAssignedToName ?? t('common.unassigned')}
                      </span>
                    </div>
                  ))}
                </div>
                <Checkbox
                  checked={reassignOpenTasks}
                  onChange={(_e, d) => setReassignOpenTasks(!!d.checked)}
                  label={t('bulkEdit.clients.reassignCheckbox', { count: affectedTasks.length, owner: ownerName })}
                />
                <Text className={styles.hint}>
                  {t('bulkEdit.clients.reassignHint')}
                </Text>
              </div>
            )}

            {phase === 'result' && result && (
              <div className={styles.form}>
                <MessageBar intent={result.failed.length === 0 ? 'success' : 'warning'}>
                  <MessageBarBody>
                    {t('bulkEdit.clients.result', { count: clientCount, succeeded: result.succeeded.length })}
                    {result.tasksReassigned > 0
                      ? ` ${t('bulkEdit.clients.tasksReassigned', { count: result.tasksReassigned })}`
                      : ''}
                  </MessageBarBody>
                </MessageBar>
                {result.failed.length > 0 && (
                  <div className={styles.taskList}>
                    {result.failed.map((f) => (
                      <div key={f.clientId} className={styles.taskRow}>
                        <span>{f.clientId}</span>
                        <span className={styles.taskMeta}>{f.error}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>

          <DialogActions>
            {phase === 'edit' && (
              <>
                <Button appearance="secondary" disabled={isBusy} onClick={() => onOpenChange(false)}>
                  {t('bulkEdit.cancel')}
                </Button>
                <Button
                  appearance="primary"
                  disabled={isBusy}
                  icon={isBusy ? <Spinner size="tiny" /> : undefined}
                  onClick={() => void handleApply()}
                >
                  {t('bulkEdit.clients.applyChanges')}
                </Button>
              </>
            )}
            {phase === 'confirmReassign' && (
              <>
                <Button appearance="secondary" disabled={isBusy} onClick={() => setPhase('edit')}>
                  {t('common.back')}
                </Button>
                <Button
                  appearance="primary"
                  disabled={isBusy}
                  icon={isBusy ? <Spinner size="tiny" /> : undefined}
                  onClick={() => void commit(reassignOpenTasks)}
                >
                  {reassignOpenTasks ? t('bulkEdit.clients.applyReassign') : t('bulkEdit.clients.applyFutureOnly')}
                </Button>
              </>
            )}
            {phase === 'result' && (
              <Button appearance="primary" onClick={() => onOpenChange(false)}>
                {t('bulkEdit.done')}
              </Button>
            )}
          </DialogActions>
          </>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
